import {Inject, Injectable} from '@angular/core';
import {
  CheckCanAcceptPaymentQuery,
  CheckCanSetupPaymentMethodQuery,
  PaymentCheckoutContextResult,
  PaymentCheckoutContextQuery,
  PaymentProviderNameEnum,
  PaymentsForCompanyQuery,
  PaymentType,
  PreparePaymentInput,
  PreparePaymentMutation,
  UploadProofOfPaymentInput,
  UploadProofOfPaymentMutation,
} from '@generated/graphql';
import {BehaviorSubject, catchError, distinctUntilChanged, EMPTY, first, map, Observable, of, Subject, tap} from 'rxjs';
import {EntityService} from '@shared/services/entity.service';
import {EntityFeatureNameEnum} from '@shared/contracts/entity-store.contract';
import {Payment} from '@modules/payment/models/payment.model';
import {PAYMENT_STORE, PaymentStore,} from '@modules/payment/store/payment.store';
import {Store} from '@ngrx/store';
import {PaymentApi} from '@modules/payment/api/payment.api';
import {paymentFactory, PaymentFactory,} from '@modules/payment/factories/payment.factory';
import {SnackBarService} from "@shared/services/snack-bar.service";
import {ScreenSpinnerService} from "@shared/component/full-screen-spinner/screen-spinner.service";

@Injectable({
  providedIn: 'root',
})
export class PaymentService extends EntityService<
  EntityFeatureNameEnum.Payment,
  PaymentType,
  Payment
> {
  readonly paid$ = new Subject<{ paymentId: string }>()
  readonly processing$ = new Subject<{ paymentId: string }>()
  readonly preparedCheckoutContext$ = new Subject<{
    orderId: string;
    checkoutContext: PaymentCheckoutContextResult['paymentCheckoutContext'];
  }>();
  private readonly processingPaymentIdsSubject = new BehaviorSubject<Set<string>>(new Set());
  private readonly processingOrderIdsSubject = new BehaviorSubject<Set<string>>(new Set());
  private readonly awaitingRefreshOrderIdsSubject = new BehaviorSubject<Set<string>>(new Set());
  readonly processingPaymentIds$ = this.processingPaymentIdsSubject.asObservable();
  readonly processingOrderIds$ = this.processingOrderIdsSubject.asObservable();
  readonly awaitingRefreshOrderIds$ = this.awaitingRefreshOrderIdsSubject.asObservable();

  constructor(
    @Inject(PAYMENT_STORE) protected readonly entityStore: PaymentStore,
    protected readonly store: Store,
    private readonly paymentApi: PaymentApi,
    private readonly uploadProofOfPaymentMutation: UploadProofOfPaymentMutation,
    private readonly preparePaymentMutation: PreparePaymentMutation,
    private readonly checkCanAcceptPaymentQuery: CheckCanAcceptPaymentQuery,
    private readonly checkCanSetupPaymentMethodQuery: CheckCanSetupPaymentMethodQuery,
    private readonly paymentCheckoutContextQuery: PaymentCheckoutContextQuery,
    private readonly _paymentsForCompanyQuery: PaymentsForCompanyQuery,
    private readonly snackBarService: SnackBarService,
    private readonly screenSpinnerService: ScreenSpinnerService,
  ) {
    super(store, entityStore);
  }

  markProcessing(paymentId: string): void {
    if (!paymentId) {
      return;
    }

    const current = this.processingPaymentIdsSubject.value;
    if (current.has(paymentId)) {
      return;
    }

    const next = new Set(current);
    next.add(paymentId);
    this.processingPaymentIdsSubject.next(next);
  }

  clearProcessing(paymentId: string): void {
    if (!paymentId) {
      return;
    }

    const current = this.processingPaymentIdsSubject.value;
    if (!current.has(paymentId)) {
      return;
    }

    const next = new Set(current);
    next.delete(paymentId);
    this.processingPaymentIdsSubject.next(next);
  }

  markOrderProcessing(orderId: string): void {
    if (!orderId) {
      return;
    }

    const current = this.processingOrderIdsSubject.value;
    if (current.has(orderId)) {
      return;
    }

    const next = new Set(current);
    next.add(orderId);
    this.processingOrderIdsSubject.next(next);
  }

  clearOrderProcessing(orderId: string): void {
    if (!orderId) {
      return;
    }

    const current = this.processingOrderIdsSubject.value;
    if (!current.has(orderId)) {
      return;
    }

    const next = new Set(current);
    next.delete(orderId);
    this.processingOrderIdsSubject.next(next);
  }

  isOrderProcessing$(orderId?: string): Observable<boolean> {
    if (!orderId) {
      return of(false);
    }

    return this.processingOrderIds$.pipe(
      map(ids => ids.has(orderId)),
      distinctUntilChanged()
    );
  }

  markOrderAwaitingRefresh(orderId: string): void {
    if (!orderId) {
      return;
    }

    const current = this.awaitingRefreshOrderIdsSubject.value;
    if (current.has(orderId)) {
      return;
    }

    const next = new Set(current);
    next.add(orderId);
    this.awaitingRefreshOrderIdsSubject.next(next);
  }

  clearOrderAwaitingRefresh(orderId: string): void {
    if (!orderId) {
      return;
    }

    const current = this.awaitingRefreshOrderIdsSubject.value;
    if (!current.has(orderId)) {
      return;
    }

    const next = new Set(current);
    next.delete(orderId);
    this.awaitingRefreshOrderIdsSubject.next(next);
  }

  isOrderAwaitingRefresh$(orderId?: string): Observable<boolean> {
    if (!orderId) {
      return of(false);
    }

    return this.awaitingRefreshOrderIds$.pipe(
      map(ids => ids.has(orderId)),
      distinctUntilChanged()
    );
  }

  isProcessing$(paymentId?: string): Observable<boolean> {
    if (!paymentId) {
      return of(false);
    }

    return this.processingPaymentIds$.pipe(
      map(ids => ids.has(paymentId)),
      distinctUntilChanged()
    );
  }

  get factory(): PaymentFactory {
    return paymentFactory;
  }

  openProofOfPayment(payment: Payment): void {
    const errorMsg = "Can't get Proof of Payment. Please try again or contact us to support!";

    if (!payment?.canViewProofOfPayment()) {
      this.snackBarService.pushErrorAlert(errorMsg);

      return;
    }

    this.screenSpinnerService.loading()

    this.paymentApi.getPaymentWithOnlyReceipt$(payment.id).pipe(
      first(),
      tap((paymentWithProof) => {
        this.screenSpinnerService.stopLoading()

        const proofUrl = paymentWithProof?.proofOfPayment?.url;

        if (proofUrl) {
          window.open(proofUrl, '_blank');

          return;
        }

        this.snackBarService.pushErrorAlert(errorMsg);
      }),
      catchError(() => {
        this.snackBarService.pushErrorAlert(errorMsg);

        this.screenSpinnerService.stopLoading()

        return EMPTY;
      }),
    ).subscribe();
  }

  openPaymentReceipt(payment: Payment): void {
    const errorMsg = "Can't get Payment Receipt. Please try again or contact us to support!";

    if (!payment?.canViewPaymentReceipt()) {
      this.snackBarService.pushErrorAlert(errorMsg);

      return;
    }

    this.screenSpinnerService.loading()

    this.paymentApi.getPaymentWithOnlyReceipt$(payment.id).pipe(
      first(),
      tap((paymentWithOnlyReceipt) => {
        this.screenSpinnerService.stopLoading()

        if (paymentWithOnlyReceipt?.receiptUrl) {
          window.open(paymentWithOnlyReceipt.receiptUrl, '_blank');

          return;
        }

        this.snackBarService.pushErrorAlert(errorMsg);
      }),
      catchError((e) => {
        this.snackBarService.pushErrorAlert(errorMsg);

        this.screenSpinnerService.stopLoading()

        return EMPTY;
      }),
    ).subscribe();
  }

  uploadProofOfPayment(data: UploadProofOfPaymentInput) {
    return this.uploadProofOfPaymentMutation
      .mutate(
        {
          input: {
            ...data,
          },
        },
        {
          context: {
            useMultipart: true,
          },
        },
      )
      .pipe(map((result) => result.data.uploadProofOfPayment));
  }

  preparePayment(input: PreparePaymentInput) {
    return this.preparePaymentMutation
      .mutate({ input })
      .pipe(
        map((result) => result.data.preparePayment),
        tap((result) => {
          if (!input.orderId || !result?.checkoutContext) {
            return;
          }

          this.preparedCheckoutContext$.next({
            orderId: input.orderId,
            checkoutContext: result.checkoutContext,
          });
        }),
      );
  }

  checkCanAcceptPayment(provider: PaymentProviderNameEnum) {
    return this.checkCanAcceptPaymentQuery
      .fetch({provider}, {fetchPolicy: 'no-cache'})
      .pipe(map((res) => res.data.checkCanAcceptPayment));
  }

  checkCanSetupPaymentMethod(provider: PaymentProviderNameEnum) {
    return this.checkCanSetupPaymentMethodQuery
      .fetch({provider}, {fetchPolicy: 'no-cache'})
      .pipe(map((res) => res.data.checkCanSetupPaymentMethod));
  }

  getCheckoutContext(orderId: string, promotionId: string | null = null) {
    return this.paymentCheckoutContextQuery
      .fetch({ orderId, promotionId }, { fetchPolicy: 'no-cache' })
      .pipe(map((res) => res.data.paymentCheckoutContext));
  }

  public getPaymentsForCompany(companyId: string): Observable<PaymentType[]> {
    return this._paymentsForCompanyQuery
      .fetch(
        {
          companyId,
        },
        {
          fetchPolicy: 'no-cache',
        },
      )
      .pipe(
        map((response) => response.data.paymentsForCompany as PaymentType[]),
      );
  }
}
